import { createTheme, responsiveFontSizes } from '@mui/material/styles';

// Create the base theme matching the reporter portal branding
let theme = createTheme({
  // Define custom breakpoints for more precise control
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  palette: {
    primary: {
      main: '#ED7420', // Magna orange
      light: '#ff9a52',
      dark: '#c85000',
    },
    secondary: {
      main: '#3A506B', // Magna blue-gray
      light: '#5d748e',
      dark: '#2c3e5a',
    },
    error: {
      main: '#E74C3C',
    },
    warning: {
      main: '#F39C12',
    },
    info: {
      main: '#3498DB',
    },
    success: {
      main: '#2ECC71',
    },
    background: {
      default: '#f5f7fa',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Segoe UI", sans-serif',
    h1: {
      fontSize: '1.75rem',
      fontWeight: 600,
      color: '#ED7420',
      '@media (min-width:600px)': {
        fontSize: '2rem',
      },
    },
    h2: {
      fontSize: '1.35rem',
      fontWeight: 600,
      color: '#3A506B',
      '@media (min-width:600px)': {
        fontSize: '1.5rem',
      },
    },
    h3: {
      fontSize: '1.1rem',
      fontWeight: 600,
      color: '#3A506B',
      '@media (min-width:600px)': {
        fontSize: '1.2rem',
      },
    },
    h4: {
      fontSize: '1rem',
      fontWeight: 600,
      color: '#3A506B',
      '@media (min-width:600px)': {
        fontSize: '1.1rem',
      },
    },
    subtitle1: {
      color: '#708181',
      fontSize: '0.9rem',
      '@media (min-width:600px)': {
        fontSize: '1rem',
      },
    },
    body1: {
      fontSize: '0.95rem',
      '@media (min-width:600px)': {
        fontSize: '1rem',
      },
    },
    body2: {
      fontSize: '0.85rem',
      '@media (min-width:600px)': {
        fontSize: '0.875rem',
      },
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          padding: '16px',
          marginBottom: '24px',
          '@media (min-width:600px)': {
            padding: '24px',
          },
          '@media (min-width:900px)': {
            padding: '32px',
            marginBottom: '32px',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 0, // Square buttons like Magna
          textTransform: 'uppercase',
          fontWeight: 500,
          letterSpacing: '0.5px',
          padding: '10px 16px',
          minWidth: '100px',
          boxShadow: 'none',
          transition: 'all 0.3s ease',
          fontSize: '0.875rem',
          '&:focus': {
            outline: 0,
          },
          '&.Mui-disabled': {
            opacity: 0.65,
            '&::before': {
              display: 'none',
            }
          },
          '@media (min-width:600px)': {
            padding: '12px 24px',
            minWidth: '120px',
            fontSize: '0.9375rem',
          },
          touchAction: 'manipulation',
          WebkitTapHighlightColor: 'transparent',
        },
        // Primary button - filled orange
        containedPrimary: {
          backgroundColor: '#ED7420 !important',
          color: '#FFFFFF !important',
          border: '1px solid #ED7420 !important',
          boxShadow: 'none !important',
          transition: 'all 0.3s ease',
          '&:hover': {
            color: '#ff6a00 !important',
            backgroundColor: '#fff !important',
            borderColor: '#ff6a00 !important',
            boxShadow: 'none !important',
          },
          '&:focus': {
            color: '#ff6a00 !important',
            backgroundColor: '#fff !important',
            borderColor: '#ff6a00 !important',
            boxShadow: 'none !important',
          }
        },
        // Secondary button - outlined orange
        containedSecondary: {
          backgroundColor: 'transparent !important',
          color: '#ED7420 !important',
          border: '1px solid #ED7420 !important',
          boxShadow: 'none !important',
          transition: 'all 0.3s ease',
          '&:hover': {
            color: '#fff !important',
            backgroundColor: '#ff6a00 !important',
            borderColor: '#ff6a00 !important',
            boxShadow: 'none !important',
          },
          '&:focus': {
            color: '#fff !important',
            backgroundColor: '#ff6a00 !important',
            borderColor: '#ff6a00 !important',
            boxShadow: 'none !important',
          }
        },
        // Outlined primary - same as secondary
        outlined: {
          borderWidth: '1px',
          padding: '12px 24px',
        },
        outlinedPrimary: {
          backgroundColor: 'transparent !important',
          color: '#ED7420 !important',
          border: '1px solid #ED7420 !important',
          boxShadow: 'none !important',
          transition: 'all 0.3s ease',
          '&:hover': {
            color: '#fff !important',
            backgroundColor: '#ff6a00 !important',
            borderColor: '#ff6a00 !important',
            boxShadow: 'none !important',
          },
          '&:focus': {
            color: '#fff !important',
            backgroundColor: '#ff6a00 !important',
            borderColor: '#ff6a00 !important',
            boxShadow: 'none !important',
          }
        },
        // Size variants
        sizeSmall: {
          padding: '8px 16px',
          fontSize: '0.875rem',
          minWidth: 'auto',
        },
        sizeLarge: {
          padding: '16px 32px',
          fontSize: '1.125rem',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          marginBottom: '16px',
          '@media (min-width:600px)': {
            marginBottom: '24px',
          },
          '& .MuiInputBase-root': {
            minHeight: '48px',
          },
          '& .MuiInputBase-input': {
            fontSize: '1rem',
            padding: '12px 14px',
          },
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          marginBottom: '16px',
          '@media (min-width:600px)': {
            marginBottom: '24px',
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          minHeight: '1.4375em',
          padding: '12px 14px',
          '&.MuiSelect-select': {
            minHeight: '1.4375em',
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: '#ED7420',
          padding: '9px',
          '&.Mui-checked': {
            color: '#ED7420',
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: '#ED7420',
          padding: '9px',
          '&.Mui-checked': {
            color: '#ED7420',
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 54,
          height: 28,
          padding: 0,
        },
        switchBase: {
          padding: 4,
          '&.Mui-checked': {
            transform: 'translateX(26px)',
            color: '#fff',
            '& + .MuiSwitch-track': {
              backgroundColor: '#ED7420',
              opacity: 1,
            },
          },
        },
        thumb: {
          width: 20,
          height: 20,
        },
        track: {
          borderRadius: 14,
          opacity: 1,
          backgroundColor: '#ccc',
        },
      },
    },
    // Table styling: zebra rows, header divider, subtle grid lines
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableRow-root': {
            backgroundColor: '#dcdfe3',
            '& .MuiTableCell-head': {
              borderBottom: '2px solid rgba(0, 0, 0, 0.2)',
              borderRight: '1px solid rgba(0, 0, 0, 0.12)',
              '&:last-child': {
                borderRight: 'none',
              },
            },
          },
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          '& .MuiTableRow-root': {
            '&:nth-of-type(even)': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
            '& .MuiTableCell-body': {
              color: 'rgba(0, 0, 0, 0.87)',
              borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
              borderRight: '1px solid rgba(0, 0, 0, 0.08)',
              '&:last-child': {
                borderRight: 'none',
              },
            },
          },
        },
      },
    },
    // Chip components for status indicators
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '4px', // Slightly rounded
          fontWeight: 500,
          textTransform: 'uppercase',
          fontSize: '0.75rem',
          letterSpacing: '0.5px',
        },
        colorPrimary: {
          backgroundColor: '#ED7420',
          color: '#fff',
        },
        colorSecondary: {
          backgroundColor: '#3A506B',
          color: '#fff',
        },
      },
    },
    // App Bar for navigation
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          color: '#3A506B',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
        },
      },
    },
    // Drawer for navigation
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#ffffff',
          borderRight: '1px solid rgba(0, 0, 0, 0.1)',
        },
      },
    },
    // List items in drawer
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(237, 116, 32, 0.12)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(255, 140, 50, 0.2)',
            '&:hover': {
              backgroundColor: 'rgba(255, 140, 50, 0.28)',
            },
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: '#3A506B',
          minWidth: '40px',
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          color: '#3A506B',
          fontWeight: 500,
        },
      },
    },
  },
});

// Apply responsive font sizes
theme = responsiveFontSizes(theme);

export default theme;